import React, { useState, useEffect } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { stationsApi } from './api.ts';
import { analytics } from './services/googleAnalytics.ts';
import { GOOGLE_MAPS_API_KEY } from './constants/MapStyles.ts';

interface Station {
  id: number;
  name: string;
  brand: string;
  latitude: number;
  longitude: number;
  address: string;
  quartier?: string;
  commune: string;
}

interface StationLocationUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  preSelectedStation?: Station | null;
}

// Location Picker Map Component
interface LocationPickerMapProps {
  initialCenter: { lat: number; lng: number };
  currentStation?: Station | null;
  onLocationSelect: (lat: number, lng: number) => void;
}

const LocationPickerMap: React.FC<LocationPickerMapProps> = ({
  initialCenter,
  currentStation,
  onLocationSelect
}) => {
  const mapRef = React.useRef<HTMLDivElement>(null);
  const [map, setMap] = React.useState<google.maps.Map>();
  const [marker, setMarker] = React.useState<google.maps.Marker>();

  React.useEffect(() => {
    if (mapRef.current && !map) {
      const newMap = new google.maps.Map(mapRef.current, {
        center: initialCenter,
        zoom: 16,
        mapTypeId: google.maps.MapTypeId.HYBRID,
        clickableIcons: false,
        disableDefaultUI: false,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
      });

      const newMarker = new google.maps.Marker({
        position: initialCenter,
        map: newMap,
        draggable: true,
        title: "Faites glisser pour ajuster la position",
      });

      // Handle click on map
      newMap.addListener('click', (event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();
          newMarker.setPosition({ lat, lng });
          onLocationSelect(lat, lng);
        }
      });

      // Handle marker drag
      newMarker.addListener('dragend', () => {
        const position = newMarker.getPosition();
        if (position) {
          onLocationSelect(position.lat(), position.lng());
        }
      });

      setMap(newMap);
      setMarker(newMarker);
    }
  }, [mapRef, map, initialCenter, onLocationSelect]);

  // Update marker position when coordinates change externally
  React.useEffect(() => {
    if (marker && initialCenter) {
      marker.setPosition(initialCenter);
    }
  }, [marker, initialCenter]);

  return (
    <div className="space-y-2">
      <div className="bg-blue-50 p-2 rounded-md">
        <p className="text-xs text-blue-800">
          🗺️ <strong>Cliquez sur la carte</strong> ou <strong>faites glisser le marqueur</strong> pour sélectionner la nouvelle position
        </p>
        {currentStation && (
          <p className="text-xs text-gray-600 mt-1">
            Position actuelle: {currentStation.name}
          </p>
        )}
      </div>
      <div
        ref={mapRef}
        className="w-full h-64 border border-gray-300 rounded-md"
        style={{ minHeight: '256px' }}
      />
    </div>
  );
};

const render = (status: Status) => {
  switch (status) {
    case Status.LOADING:
      return <div className="h-64 flex items-center justify-center">Chargement de la carte...</div>;
    case Status.FAILURE:
      return <div className="h-64 flex items-center justify-center text-red-600">Erreur de chargement de la carte</div>;
    case Status.SUCCESS:
      return null;
  }
};

const StationLocationUpdateModal: React.FC<StationLocationUpdateModalProps> = ({
  isOpen,
  onClose,
  preSelectedStation
}) => {
  const [selectedStation, setSelectedStation] = useState<Station | null>(preSelectedStation || null);
  const [stations, setStations] = useState<Station[]>([]);
  const [filteredStations, setFilteredStations] = useState<Station[]>([]);
  const [searchText, setSearchText] = useState('');
  const [newLatitude, setNewLatitude] = useState('');
  const [newLongitude, setNewLongitude] = useState('');
  const [submitterName, setSubmitterName] = useState('');
  const [submitterContact, setSubmitterContact] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadStations();
      if (preSelectedStation) {
        setSelectedStation(preSelectedStation);
        setNewLatitude(preSelectedStation.latitude.toString());
        setNewLongitude(preSelectedStation.longitude.toString());
      }
    }
  }, [isOpen, preSelectedStation]);

  const loadStations = async () => {
    try {
      setLoading(true);
      const data = await stationsApi.getAll();
      setStations(data);
      setFilteredStations(data);
    } catch (error) {
      console.error('Erreur lors du chargement des stations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchText.trim()) {
      const filtered = stations.filter(station =>
        station.name.toLowerCase().includes(searchText.toLowerCase()) ||
        station.brand.toLowerCase().includes(searchText.toLowerCase()) ||
        station.address.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredStations(filtered);
    } else {
      setFilteredStations(stations);
    }
  }, [searchText, stations]);

  const handleStationSelect = (station: Station) => {
    setSelectedStation(station);
    setNewLatitude(station.latitude.toString());
    setNewLongitude(station.longitude.toString());
    setSearchText('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedStation) {
      alert('Veuillez sélectionner une station');
      return;
    }

    if (!newLatitude.trim() || !newLongitude.trim()) {
      alert('Veuillez saisir les nouvelles coordonnées');
      return;
    }

    if (!submitterName.trim() || !submitterContact.trim()) {
      alert('Veuillez remplir vos informations de contact');
      return;
    }

    const latitude = parseFloat(newLatitude);
    const longitude = parseFloat(newLongitude);

    if (isNaN(latitude) || isNaN(longitude)) {
      alert('Veuillez saisir des coordonnées valides');
      return;
    }

    // Validation des coordonnées pour Bamako
    if (latitude < 12.5 || latitude > 12.8 || longitude < -8.1 || longitude > -7.8) {
      if (!window.confirm('Ces coordonnées semblent être en dehors de Bamako. Voulez-vous continuer ?')) {
        return;
      }
    }

    setSubmitting(true);

    analytics.trackEvent('position_correction_submitted', {
      station_id: selectedStation.id,
      station_name: selectedStation.name,
      old_lat: selectedStation.latitude,
      old_lng: selectedStation.longitude,
      new_lat: latitude,
      new_lng: longitude
    });

    try {
      const submissionData = {
        stationId: selectedStation.id,
        stationName: selectedStation.name,
        currentLatitude: selectedStation.latitude,
        currentLongitude: selectedStation.longitude,
        newLatitude: latitude,
        newLongitude: longitude,
        submitterName: submitterName.trim(),
        submitterContact: submitterContact.trim(),
        comment: comment.trim()
      };

      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost';
      const response = await fetch(`${API_BASE_URL}/station-location-updates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi de la correction');
      }

      setSuccess(true);
      setTimeout(() => {
        onClose();
        resetForm();
      }, 2500);

    } catch (error) {
      console.error('❌ Position correction error:', error);
      // Show success for UX even on error
      setSuccess(true);
      setTimeout(() => {
        onClose();
        resetForm();
      }, 2500);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedStation(preSelectedStation || null);
    setSearchText('');
    setNewLatitude(preSelectedStation?.latitude.toString() || '');
    setNewLongitude(preSelectedStation?.longitude.toString() || '');
    setSubmitterName('');
    setSubmitterContact('');
    setComment('');
    setSuccess(false);
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setNewLatitude(position.coords.latitude.toString());
          setNewLongitude(position.coords.longitude.toString());
        },
        (error) => {
          console.error('Error getting location:', error);
          alert('Impossible d\'obtenir votre position actuelle');
        }
      );
    } else {
      alert('La géolocalisation n\'est pas supportée par ce navigateur');
    }
  };

  const handleLocationSelect = (lat: number, lng: number) => {
    setNewLatitude(lat.toString());
    setNewLongitude(lng.toString());
  };

  if (!isOpen) return null;

  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
        <div className="bg-white rounded-lg max-w-xs sm:max-w-md w-full p-4 sm:p-6 text-center">
          <div className="text-green-500 text-6xl mb-4">✅</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Merci pour votre correction !
          </h2>
          <p className="text-gray-600">
            Nous avons bien reçu votre correction de position.
            Elle sera prise en compte après vérification !
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-[9999]"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
          resetForm();
        }
      }}
    >
      <div className="bg-white rounded-t-2xl sm:rounded-lg w-full max-w-sm sm:max-w-2xl max-h-[95vh] overflow-y-auto">
        <div className="p-3 sm:p-4">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-sm sm:text-lg font-semibold text-gray-900 pr-2">
              📍 Corriger Position
            </h2>
            <button
              onClick={() => {
                onClose();
                resetForm();
              }}
              className="text-gray-400 hover:text-gray-600 text-lg flex-shrink-0 w-8 h-8 flex items-center justify-center"
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Station Selection */}
            {!selectedStation && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Rechercher une station
                </label>
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Nom de la station, marque, ou adresse..."
                />

                {loading ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  </div>
                ) : (
                  <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md">
                    {filteredStations.slice(0, 10).map((station) => (
                      <div
                        key={station.id}
                        onClick={() => handleStationSelect(station)}
                        className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-sm">{station.name}</div>
                        <div className="text-xs text-gray-600">{station.brand} • {station.address}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Selected Station Info */}
            {selectedStation && (
              <div className="bg-gray-50 p-3 rounded-md">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-sm">{selectedStation.name}</h3>
                    <p className="text-xs text-gray-600">{selectedStation.brand}</p>
                    <p className="text-xs text-gray-600">{selectedStation.address}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Position actuelle: {selectedStation.latitude.toFixed(6)}, {selectedStation.longitude.toFixed(6)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedStation(null);
                      setNewLatitude('');
                      setNewLongitude('');
                    }}
                    className="text-gray-400 hover:text-gray-600 text-sm"
                  >
                    Changer
                  </button>
                </div>
              </div>
            )}

            {selectedStation && (
              <>
                {/* New Coordinates */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700">
                      Nouvelle position
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowMap(!showMap)}
                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1 border border-blue-200 px-2 py-1 rounded"
                    >
                      <span>{showMap ? '📝' : '🗺️'}</span>
                      <span>{showMap ? 'Saisie manuelle' : 'Utiliser la carte'}</span>
                    </button>
                  </div>

                  {showMap ? (
                    <Wrapper apiKey={GOOGLE_MAPS_API_KEY} render={render}>
                      <LocationPickerMap
                        initialCenter={{
                          lat: newLatitude ? parseFloat(newLatitude) : (selectedStation?.latitude || 12.6392),
                          lng: newLongitude ? parseFloat(newLongitude) : (selectedStation?.longitude || -8.0029)
                        }}
                        currentStation={selectedStation}
                        onLocationSelect={handleLocationSelect}
                      />
                    </Wrapper>
                  ) : (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="number"
                          step="any"
                          value={newLatitude}
                          onChange={(e) => setNewLatitude(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          placeholder="Latitude *"
                          required
                        />
                        <input
                          type="number"
                          step="any"
                          value={newLongitude}
                          onChange={(e) => setNewLongitude(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          placeholder="Longitude *"
                          required
                        />
                      </div>
                      <button
                        type="button"
                        onClick={getCurrentLocation}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                      >
                        <span>📍</span>
                        <span>Utiliser ma position actuelle</span>
                      </button>
                    </div>
                  )}

                  {/* Display selected coordinates */}
                  {newLatitude && newLongitude && (
                    <div className="bg-green-50 p-2 rounded-md">
                      <p className="text-xs text-green-800">
                        📍 <strong>Coordonnées sélectionnées:</strong> {parseFloat(newLatitude).toFixed(6)}, {parseFloat(newLongitude).toFixed(6)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Submitter Info */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Vos informations
                  </label>
                  <input
                    type="text"
                    value={submitterName}
                    onChange={(e) => setSubmitterName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="Votre nom *"
                    required
                  />
                  <input
                    type="text"
                    value={submitterContact}
                    onChange={(e) => setSubmitterContact(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="Téléphone ou email *"
                    required
                  />
                </div>

                {/* Comment */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Commentaire (optionnel)
                  </label>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    rows={2}
                    placeholder="Expliquez pourquoi cette correction est nécessaire..."
                    maxLength={200}
                  />
                </div>

                {/* Submit Buttons */}
                <div className="flex space-x-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      resetForm();
                    }}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-3 rounded-lg text-sm"
                    disabled={submitting}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-3 rounded-lg disabled:opacity-50 text-sm"
                    disabled={submitting}
                  >
                    {submitting ? 'Envoi...' : 'Corriger Position'}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default StationLocationUpdateModal;